import React, { useState } from "react";
import { Check, Copy, Link2, Loader2 } from "lucide-react";
import Dialog from "./Dialog";
import Switch from "./Switch";
import { ShareLinkState } from "../hooks/useShareLink";
import { shareUrl } from "../firebase/share";
import { FEED_ENABLED } from "../utils/features";

/** Private link to one finished debate, separate from the public feed. */
export default function ShareLinkButton({ state }: { state: ShareLinkState }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-line bg-surface text-sm font-semibold text-content hover:bg-subtle active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,transform] duration-150"
      >
        <Link2 size={15} aria-hidden="true" />
        {state.shareId ? "Shared link" : "Share link"}
      </button>
      <ShareLinkDialog state={state} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function ShareLinkDialog({ state, open, onClose }: { state: ShareLinkState; open: boolean; onClose: () => void }) {
  const [anonymous, setAnonymous] = useState(false);
  const [copied, setCopied] = useState(false);
  const { shareId, busy, error } = state;
  const url = shareId ? shareUrl(shareId) : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      document.getElementById("share-link-url")?.focus();
    }
  };

  return (
      <Dialog open={open} onClose={onClose} title="Share this debate">
        {shareId ? (
          <>
            <p className="text-sm text-muted leading-6">
              Anyone with this link can read the debate.{FEED_ENABLED && " It isn't listed on the public feed."}
            </p>
            <div className="flex items-center gap-2">
              <input
                id="share-link-url"
                readOnly
                value={url}
                aria-label="Share link"
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 h-10 px-3 rounded-md border border-line bg-subtle font-mono text-sm text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              />
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md bg-action text-on-solid text-sm font-semibold hover:bg-action-hover active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 transition-[background-color,transform] duration-150"
              >
                {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}
            <button
              type="button"
              disabled={busy}
              onClick={() => state.revoke()}
              className="button-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {busy ? "Turning off…" : "Turn off link"}
            </button>
            <p className="text-xs text-muted">Turning it off stops the link working right away. A new link will have a different address.</p>
          </>
        ) : (
          <>
            <p className="text-sm text-muted leading-6">
              Create a link that lets anyone you send it to read this debate, including people without an account.{FEED_ENABLED && " It won't appear on the public feed."} Attached images stay private.
            </p>
            <div className="flex items-center justify-between gap-4 py-3 border-y border-line">
              <span className="text-sm text-content">Hide my first name</span>
              <Switch checked={anonymous} onChange={setAnonymous} label="Hide my first name" />
            </div>
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}
            <button
              type="button"
              className="button-primary w-full"
              disabled={busy}
              onClick={() => state.create(anonymous)}
            >
              {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              {busy ? "Creating link…" : "Create link"}
            </button>
          </>
        )}
      </Dialog>
  );
}
